import { Link } from '@tanstack/react-router'
import { Plus, X } from 'lucide-react'
import type { ReactNode } from 'react'

import type { ChannelDestinations, InstagramDestination } from '@/api/endpoints/destinations'
import { Callout } from '@/components/ui/Callout'
import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'
import { Input } from '@/components/ui/Input'
import { Segmented } from '@/components/ui/Segmented'
import { Toggle } from '@/components/ui/Toggle'
import {
  blockReason,
  blockReasonText,
  breakerAdvice,
  breakerLabel,
  displayHandle,
  type BlockReason,
} from '@/features/platforms/destinations/model'
import { VideoBrandSuggestion } from '@/features/platforms/destinations/VideoBrandSuggestion'
import { DestinationPostsQueue } from './DestinationPostsQueue'
import { MAX_CTA_CHARS, MAX_PAUSE_WINDOWS, type DestinationValues } from './destinations'
import { relativeWhen } from './model'
import { Disclosure, Field, NumberInput, Section } from './parts'
import styles from './ChannelEditDialog.module.scss'

export interface DestinationsTabProps {
  slug: string
  /** O @ do canal (YouTube), para a sugestão da marca no vídeo. */
  channelTag?: string | null | undefined
  destinations: ChannelDestinations
  instagram: InstagramDestination
  values: DestinationValues
  /** ``enabled`` como está gravado (o que o formulário carregou). */
  savedEnabled: boolean
  errors: Record<string, string>
  set: <K extends keyof DestinationValues>(key: K, value: DestinationValues[K]) => void
  before: (key: keyof DestinationValues) => string | undefined
  /** Fuso do ciclo de upload: as pausas são lidas e escritas nele. */
  timezone: string
  /** O destino no servidor não é o da tela. ``null`` = nada a avisar. */
  serverChange?: DestinationServerChange | null | undefined
  onDismissServerChange?: (() => void) | undefined
}

/**
 * - ``rebased``: o servidor mudou com edição não salva na aba; ``fields`` são
 *   os campos editados que também mudaram lá (a edição do dono foi mantida).
 * - ``saved``: o PUT gravou o que o dono mudou, mas devolveu o destino
 *   diferente da tela nestes ``fields`` (ex.: o disjuntor o desligou).
 */
export interface DestinationServerChange {
  kind: 'rebased' | 'saved'
  fields: string[]
}

function ServerChangeNotice({ change, onDismiss }: { change: DestinationServerChange; onDismiss?: (() => void) | undefined }) {
  const list = change.fields.join(', ')
  return (
    <Callout
      className={styles.topNotice}
      role="alert"
      variant={change.kind === 'saved' ? 'danger' : 'warning'}
      title={
        change.kind === 'saved'
          ? 'Salvo, mas o destino não ficou como a tela mostrava'
          : 'O destino mudou no servidor enquanto esta aba estava aberta'
      }
      action={
        onDismiss ? (
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Entendi
          </Button>
        ) : undefined
      }
    >
      {change.kind === 'saved' ? (
        <p>
          O que você mudou foi gravado, mas o servidor tem outro valor em: {list}. Por exemplo, o disjuntor pode ter
          desligado o destino enquanto a aba estava aberta. A aba mostra agora o que está gravado.
        </p>
      ) : (
        <p>
          O formulário foi atualizado com o valor novo em tudo o que você não editou; o que você editou foi mantido
          inteiro (o modo junto com o número).
          {change.fields.length ? ` Também mudou lá: ${list}. Confira antes de salvar.` : ' Confira antes de salvar.'}
        </p>
      )}
    </Callout>
  )
}

/**
 * Aba "Destinos": o Instagram espelha o que o YouTube publica — mesmo vídeo,
 * junto com ele ou N minutos depois. A conta é conectada na tela do canal;
 * aqui o destino é ligado e configurado, e a fila mostra cada post com o
 * motivo de ter parado. Ver docs/plan-platform-destinations.md.
 */
export function DestinationsTab(props: DestinationsTabProps) {
  const { slug, instagram, values, errors, set, before } = props
  const blocked = blockReason(instagram)
  const username = instagram.account ? displayHandle(instagram.account.username) : ''
  // O disjuntor desligou o destino (o dado é o do servidor, relido): religar
  // é decisão explícita, pelo botão do aviso, nunca um toggle de passagem.
  const tripped = !instagram.enabled && instagram.disabled_reason !== ''
  // Ligar exige conta ativa, publicador e disjuntor resolvido; desligar nunca é bloqueado.
  const toggleLocked = (blocked !== null || tripped) && !values.enabled

  return (
    <div className={styles.tabPanel}>
      {props.serverChange ? (
        <ServerChangeNotice change={props.serverChange} onDismiss={props.onDismissServerChange} />
      ) : null}

      <VideoBrandSuggestion
        className={styles.topNotice}
        slug={slug}
        channelTag={props.channelTag}
        destinations={props.destinations}
      />

      <Section>
        <div className={styles.toggleHead}>
          <Toggle
            id="ig-enabled"
            aria-label="Publicar também no Instagram"
            checked={values.enabled}
            disabled={toggleLocked}
            onCheckedChange={(v) => {
              set('enabled', v)
            }}
          />
          <div className={styles.toggleHeadText}>
            <span className={styles.labelRow}>
              <h3 className={styles.sectionTitle}>Publicar também no Instagram</h3>
              {before('enabled') !== undefined ? (
                <span className={`${styles.meta} ${styles.metaChanged}`}>antes: {before('enabled')}</span>
              ) : null}
            </span>
            {tripped && !values.enabled ? (
              <span className={styles.error}>
                Desligado pelo disjuntor: {breakerLabel(instagram.disabled_reason)}.
              </span>
            ) : (
              <span className={styles.hint}>
                {username
                  ? `Os mesmos vídeos que o ciclo de upload manda ao YouTube saem como Reels em ${username}.`
                  : 'Os mesmos vídeos que o ciclo de upload manda ao YouTube saem como Reels no perfil conectado.'}{' '}
                Upload manual não é espelhado.
              </span>
            )}
            {props.savedEnabled && !values.enabled ? (
              <span className={styles.error} role="alert">
                Ao salvar, o destino desliga e os posts que ainda não começaram a ser enviados são cancelados.
              </span>
            ) : null}
          </div>
        </div>
        {blocked ? <BlockNotice slug={slug} reason={blocked} /> : null}
        {tripped ? (
          <Callout
            variant="danger"
            title="O destino se desligou sozinho"
            action={
              !values.enabled && blocked === null ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    set('enabled', true)
                  }}
                >
                  Religar o destino
                </Button>
              ) : undefined
            }
          >
            <p>
              {instagram.disabled_at ? `${relativeWhen(instagram.disabled_at)}: ` : ''}
              {breakerLabel(instagram.disabled_reason)}. Os posts pendentes foram cancelados.{' '}
              {breakerAdvice(instagram.disabled_reason)}
            </p>
            {values.enabled ? (
              <p>Ao salvar, o destino volta a publicar e o motivo do desligamento é apagado.</p>
            ) : null}
          </Callout>
        ) : null}
      </Section>

      <Section title="Quando">
        <Field
          groupId="ig-timing"
          label="Horário"
          help={
            'O Instagram não agenda: o Prism prepara o Reel até 45 min antes e publica a partir do horário. ' +
            'Posts da mesma conta saem com pelo menos 20 min entre eles, e o que atrasar mais de 3 h não sai mais. ' +
            'Não contam nessas 3 h, até 24 h: o servidor desligado e, antes do envio começar, a espera pelo ' +
            'YouTube ou pelo juiz da legenda (sem rede, por exemplo).'
          }
          before={before('timing_mode') ?? before('offset_minutes')}
          error={errors.offset_minutes}
          hint="Sai a partir desse horário, com pelo menos 20 min entre posts da mesma conta."
        >
          <div className={styles.choiceRow}>
            <Segmented
              aria-label="Quando publicar no Instagram"
              options={[
                { value: 'with_primary', label: 'Junto com o YouTube' },
                { value: 'offset', label: 'Minutos depois' },
              ]}
              value={values.timing_mode}
              onValueChange={(v) => {
                set('timing_mode', v)
              }}
            />
            {values.timing_mode === 'offset' ? (
              <NumberInput
                id="ig-offset"
                aria-label="Minutos depois do YouTube"
                min={0}
                max={720}
                suffix="min · 0–720"
                value={values.offset_minutes}
                error={Boolean(errors.offset_minutes)}
                onChange={(v) => {
                  set('offset_minutes', v ?? Number.NaN)
                }}
              />
            ) : null}
          </div>
        </Field>
      </Section>

      <Section title="Quais vídeos">
        <Field
          groupId="ig-volume"
          label="Quantos por dia"
          before={before('max_mode') ?? before('max_per_day')}
          error={errors.max_per_day}
          hint="O ciclo sobe em ordem de seleção: com limite, saem os primeiros do dia, os de maior nota."
        >
          <div className={styles.choiceRow}>
            <Segmented
              aria-label="Quantos vídeos por dia no Instagram"
              options={[
                { value: 'all', label: 'Todos os do YouTube' },
                { value: 'limit', label: 'No máximo' },
              ]}
              value={values.max_mode}
              onValueChange={(v) => {
                set('max_mode', v)
              }}
            />
            {values.max_mode === 'limit' ? (
              <NumberInput
                id="ig-max"
                aria-label="Máximo de vídeos por dia"
                min={1}
                max={10}
                suffix="por dia · 1–10"
                value={values.max_per_day}
                error={Boolean(errors.max_per_day)}
                onChange={(v) => {
                  set('max_per_day', v ?? Number.NaN)
                }}
              />
            ) : null}
          </div>
        </Field>
        <ToggleRow
          id="ig-safety"
          label="Só vídeos aprovados pela segurança"
          checked={values.require_safety_approved}
          before={before('require_safety_approved')}
          onChange={(v) => {
            set('require_safety_approved', v)
          }}
          hint={
            values.require_safety_approved
              ? 'Exige o veredito aprovado. O vídeo em revisão vira "pulado" na fila, com o motivo, e volta sozinho se for aprovado antes do horário.'
              : 'Desligado, sai no Instagram tudo o que o YouTube publica, inclusive vídeo que a segurança mandou para revisão. Só o bloqueado fica de fora.'
          }
        />
      </Section>

      <Section title="Legenda">
        <p className={styles.hint}>
          Montada do título e da descrição do YouTube, sem links, sem @ de terceiros e sem o "inscreva-se".
        </p>
        <div className={styles.grid2}>
          <Field
            id="ig-cta"
            label="Chamada no fim"
            meta={values.caption_cta.trim() ? `${String(values.caption_cta.length)}/${String(MAX_CTA_CHARS)}` : 'padrão'}
            before={before('caption_cta')}
            error={errors.caption_cta}
            hint="Vazio usa o padrão."
          >
            <Input
              id="ig-cta"
              maxLength={MAX_CTA_CHARS}
              placeholder={username ? `Siga ${username} para mais.` : 'Siga @perfil para mais.'}
              value={values.caption_cta}
              error={Boolean(errors.caption_cta)}
              onChange={(e) => {
                set('caption_cta', e.target.value)
              }}
            />
          </Field>
          <Field
            id="ig-hashtags"
            label="Hashtags"
            help="O Instagram limita a 5 hashtags por post."
            before={before('hashtags_max')}
            error={errors.hashtags_max}
          >
            <NumberInput
              id="ig-hashtags"
              min={0}
              max={5}
              suffix="no máximo · 0–5"
              value={values.hashtags_max}
              error={Boolean(errors.hashtags_max)}
              onChange={(v) => {
                set('hashtags_max', v ?? Number.NaN)
              }}
            />
          </Field>
        </div>
        <ToggleRow
          id="ig-credit"
          label="Crédito da fonte"
          checked={values.include_source_credit}
          before={before('include_source_credit')}
          onChange={(v) => {
            set('include_source_credit', v)
          }}
          hint={'"Corte de: <título do episódio> (no YouTube)", sem link.'}
        />
      </Section>

      <PauseWindows {...props} />

      <Disclosure
        title="Avançado"
        meta={values.share_to_feed && !values.trial ? '2 opções · usando padrão' : 'valores personalizados'}
      >
        <ToggleRow
          id="ig-feed"
          label="Mostrar também no feed"
          checked={values.share_to_feed}
          before={before('share_to_feed')}
          onChange={(v) => {
            set('share_to_feed', v)
          }}
          hint="Desligado, o Reel aparece só na aba Reels do perfil."
        />
        <ToggleRow
          id="ig-trial"
          label="Reel de teste"
          checked={values.trial}
          before={before('trial')}
          onChange={(v) => {
            set('trial', v)
          }}
          hint="O Reel vai primeiro para quem não segue o perfil, e não aparece para os seguidores."
        />
      </Disclosure>

      <DestinationPostsQueue slug={slug} destinationEnabled={instagram.enabled} />
    </div>
  )
}

function BlockNotice({ slug, reason }: { slug: string; reason: BlockReason }) {
  const needsAccount = reason === 'no_account' || reason === 'account_expired' || reason === 'account_revoked'
  return (
    <Callout
      variant={reason === 'account_revoked' || reason === 'account_expired' ? 'warning' : 'neutral'}
      title={needsAccount ? 'Falta a conta do Instagram' : 'O destino não pode ser ligado neste servidor'}
      action={
        needsAccount ? (
          // Em outra aba: sair daqui fecharia o modal sem o "Sair sem salvar?".
          <Link
            to="/channels"
            search={{ channel: slug }}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.linkButton}
          >
            {reason === 'no_account' ? 'Conectar na tela do canal' : 'Reconectar na tela do canal'}
          </Link>
        ) : undefined
      }
    >
      <p>{blockReasonText(reason)}</p>
    </Callout>
  )
}

function ToggleRow({
  id,
  label,
  checked,
  before,
  hint,
  onChange,
}: {
  id: string
  label: string
  checked: boolean
  before: string | undefined
  hint: ReactNode
  onChange: (value: boolean) => void
}) {
  return (
    <div className={styles.toggleHead}>
      <Toggle id={id} aria-label={label} checked={checked} onCheckedChange={onChange} />
      <div className={styles.toggleHeadText}>
        <span className={styles.labelRow}>
          <span className={styles.typeName}>{label}</span>
          {before !== undefined ? (
            <span className={`${styles.meta} ${styles.metaChanged}`}>antes: {before}</span>
          ) : null}
        </span>
        <span className={styles.hint}>{hint}</span>
      </div>
    </div>
  )
}

function PauseWindows({ values, errors, set, before, timezone }: DestinationsTabProps) {
  const windows = values.pause_windows
  const changed = before('pause_windows')
  return (
    <Section title="Pausas" meta={`${String(windows.length)}/${String(MAX_PAUSE_WINDOWS)}`}>
      <p className={styles.hint}>
        Períodos sem publicar no Instagram, no fuso {timezone} (ex.: 22/10 00:00 → 26/10 23:59). O post que cair numa
        pausa vira "pulado", com o motivo.
      </p>
      {changed !== undefined ? <p className={`${styles.hint} ${styles.metaChanged}`}>antes: {changed}</p> : null}
      {windows.length ? (
        <div className={styles.pauseList}>
          {windows.map((w, i) => {
            const error = errors[`pause_windows.${String(i)}`]
            const n = String(i + 1)
            return (
              // Sem id estável: a ordem é a da lista, e os campos são controlados.
              <div key={i}>
                <div className={styles.pauseRow}>
                  <Input
                    id={`ig-pause-${String(i)}-from`}
                    type="datetime-local"
                    aria-label={`Início da pausa ${n}`}
                    value={w.from}
                    error={Boolean(error)}
                    onChange={(e) => {
                      set(
                        'pause_windows',
                        windows.map((x, j) => (j === i ? { ...x, from: e.target.value } : x)),
                      )
                    }}
                  />
                  <span aria-hidden>→</span>
                  <Input
                    id={`ig-pause-${String(i)}-until`}
                    type="datetime-local"
                    aria-label={`Fim da pausa ${n}`}
                    value={w.until}
                    error={Boolean(error)}
                    onChange={(e) => {
                      set(
                        'pause_windows',
                        windows.map((x, j) => (j === i ? { ...x, until: e.target.value } : x)),
                      )
                    }}
                  />
                  <IconButton
                    variant="ghost"
                    aria-label={`Remover a pausa ${n}`}
                    onClick={() => {
                      set(
                        'pause_windows',
                        windows.filter((_, j) => j !== i),
                      )
                    }}
                  >
                    <X />
                  </IconButton>
                </div>
                {error ? (
                  <p className={styles.error} role="alert">
                    {error}
                  </p>
                ) : null}
              </div>
            )
          })}
        </div>
      ) : (
        <span className={styles.empty}>Nenhuma pausa.</span>
      )}
      {windows.length < MAX_PAUSE_WINDOWS ? (
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<Plus />}
          className={styles.addPause}
          onClick={() => {
            set('pause_windows', [...windows, { from: '', until: '' }])
          }}
        >
          Adicionar pausa
        </Button>
      ) : null}
    </Section>
  )
}
